"use client";

import { sendGTMEvent } from '@next/third-parties/google';

interface Item {
    unique_id: string;
    title: string;
    price: number;
    quantity: number;
}

interface DataLayerInterface {
    pushToDataLayer(): void;
}

class ViewItemDataLayer implements DataLayerInterface {
    constructor(
        private product: any
    ) {}

    pushToDataLayer() {
        const items = Array.isArray(this.product.bundle_items)
            ? this.product.bundle_items
            : [{...this.product, quantity: 1}];

        const totalValue = items.reduce((sum: number, item: any) => {
            const itemPrice = item.sale_price || item.price || 0;
            return sum + (itemPrice * (item.quantity || 1));
        }, 0);

        sendGTMEvent({
            event: 'view_item',
            ecommerce: {
                currency: 'IRR',
                value: totalValue,
                items: items.map((item: any) => {
                    const itemPrice = item.sale_price || item.price || 0;
                    const originalPrice = item.price || itemPrice;
                    const discount = originalPrice - itemPrice;
                    const categories = item.categories || this.product.categories || [];

                    const itemData: any = {
                        item_id: `${item.unique_id || item.id}`,
                        item_name: item.title || `Product ${item.unique_id || item.id}`,
                        item_brand: item.brand || this.product.brand || '',
                        price: itemPrice,
                        discount: discount > 0 ? discount : undefined,
                        quantity: item.quantity || 1,
                    };

                    categories.forEach((category: string, index: number) => {
                        itemData[`item_category${index === 0 ? '' : index + 1}`] = category;
                    });

                    return itemData;
                }),
            },
        });
    }
}

class AddToCartDataLayer implements DataLayerInterface {
    constructor(private items: Item[]) {}

    pushToDataLayer() {
        const totalValue = this.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        sendGTMEvent({
            event: 'add_to_cart',
            ecommerce: {
                currency: 'IRR',
                value: totalValue,
                items: this.items.map(item => ({
                    item_id: item.unique_id,
                    item_name: item.title,
                    price: item.price,
                    quantity: item.quantity,
                })),
            },
        });
    }
}

class CartItemDataLayer implements DataLayerInterface {
    constructor(private items: Item[], private eventType: 'view_cart' | 'begin_checkout' = 'view_cart') {}

    pushToDataLayer() {
        const totalValue = this.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        sendGTMEvent({
            event: this.eventType,
            ecommerce: {
                currency: 'IRR',
                value: totalValue,
                items: this.items.map(item => ({
                    item_id: item.unique_id,
                    item_name: item.title,
                    price: item.price,
                    quantity: item.quantity,
                })),
            },
        });
    }
}

class CheckoutDataLayer implements DataLayerInterface {
    constructor(private paymentType: string, private items: Item[]) {}

    pushToDataLayer() {
        const totalValue = this.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        sendGTMEvent({
            event: 'begin_checkout',
            ecommerce: {
                currency: 'IRR',
                value: totalValue,
                payment_type: this.paymentType,
                items: this.items.map(item => ({
                    item_id: item.unique_id,
                    item_name: item.title,
                    price: item.price,
                    quantity: item.quantity,
                })),
            },
        });
    }
}

class PurchaseDataLayer implements DataLayerInterface {
    constructor(private objectId: number, private items: Item[]) {}

    pushToDataLayer() {
        const totalValue = this.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        sendGTMEvent({
            event: 'purchase',
            ecommerce: {
                transaction_id: `${this.objectId}`,
                currency: 'IRR',
                value: totalValue,
                items: this.items.map(item => ({
                    item_id: item.unique_id,
                    item_name: item.title,
                    price: item.price,
                    quantity: item.quantity,
                })),
            },
        });
    }
}

class LoginDataLayer implements DataLayerInterface {
    constructor(private userId: string, private method?: string) {}

    pushToDataLayer() {
        sendGTMEvent({
            event: 'login',
            user_id: this.userId,
            method: this.method || undefined,
        });
    }
}

class SearchDataLayer implements DataLayerInterface {
    constructor(private searchTerm: string) {}

    pushToDataLayer() {
        sendGTMEvent({
            event: 'search',
            search_term: this.searchTerm,
        });
    }
}

class ShareDataLayer implements DataLayerInterface {
    constructor(private method?: string, private contentType?: string, private itemId?: string) {}

    pushToDataLayer() {
        sendGTMEvent({
            event: 'share',
            method: this.method || undefined,
            content_type: this.contentType || undefined,
            item_id: this.itemId || undefined,
        });
    }
}

class SignUpDataLayer implements DataLayerInterface {
    constructor(private userId: string, private method?: string) {}

    pushToDataLayer() {
        sendGTMEvent({
            event: 'sign_up',
            user_id: this.userId,
            method: this.method || undefined,
        });
    }
}

class ViewItemListDataLayer implements DataLayerInterface {
    constructor(private items: Item[], private itemListId?: string, private itemListName?: string) {}

    pushToDataLayer() {
        sendGTMEvent({
            event: 'view_item_list',
            ecommerce: {
                currency: 'IRR',
                item_list_id: this.itemListId || undefined,
                item_list_name: this.itemListName || undefined,
                items: this.items.map((item, index) => ({
                    item_id: item.unique_id,
                    item_name: item.title,
                    price: item.price,
                    quantity: item.quantity,
                    index,
                })),
            },
        });
    }
}

class SelectItemDataLayer implements DataLayerInterface {
    constructor(private item: Item, private itemListId?: string, private itemListName?: string) {}

    pushToDataLayer() {
        sendGTMEvent({
            event: 'select_item',
            ecommerce: {
                item_list_id: this.itemListId || undefined,
                item_list_name: this.itemListName || undefined,
                items: [{
                    item_id: this.item.unique_id,
                    item_name: this.item.title,
                    price: this.item.price,
                    quantity: this.item.quantity,
                }],
            },
        });
    }
}

class ViewPromotionDataLayer implements DataLayerInterface {
    constructor(
        private item: Item,
        private creativeName?: string,
        private creativeSlot?: string,
        private promotionId?: string,
        private promotionName?: string
    ) {}

    pushToDataLayer() {
        sendGTMEvent({
            event: 'view_promotion',
            ecommerce: {
                creative_name: this.creativeName || undefined,
                creative_slot: this.creativeSlot || undefined,
                promotion_id: this.promotionId || undefined,
                promotion_name: this.promotionName || undefined,
                items: [{
                    item_id: this.item.unique_id,
                    item_name: this.item.title,
                    price: this.item.price,
                    quantity: this.item.quantity,
                }],
            },
        });
    }
}

class SelectPromotionDataLayer implements DataLayerInterface {
    constructor(
        private item: Item,
        private creativeName?: string,
        private creativeSlot?: string,
        private promotionId?: string,
        private promotionName?: string
    ) {}

    pushToDataLayer() {
        sendGTMEvent({
            event: 'select_promotion',
            ecommerce: {
                creative_name: this.creativeName || undefined,
                creative_slot: this.creativeSlot || undefined,
                promotion_id: this.promotionId || undefined,
                promotion_name: this.promotionName || undefined,
                items: [{
                    item_id: this.item.unique_id,
                    item_name: this.item.title,
                    price: this.item.price,
                    quantity: this.item.quantity,
                }],
            },
        });
    }
}

class AddToWishlistDataLayer implements DataLayerInterface {
    constructor(private item: Item) {}

    pushToDataLayer() {
        sendGTMEvent({
            event: 'add_to_wishlist',
            ecommerce: {
                currency: 'IRR',
                value: 1,
                items: {
                    item_id: this.item.unique_id,
                    item_name: this.item.title,
                    price: this.item.price,
                    quantity: 1,
                },
            },
        });
    }
}

class RemoveFromCartDataLayer implements DataLayerInterface {
    constructor(private items: Item[]) {}

    pushToDataLayer() {
        const totalValue = this.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        sendGTMEvent({
            event: 'remove_from_cart',
            ecommerce: {
                currency: 'IRR',
                value: totalValue,
                items: this.items.map(item => ({
                    item_id: item.unique_id,
                    item_name: item.title,
                    price: item.price,
                    quantity: item.quantity,
                })),
            },
        });
    }
}

export class DataLayerFactory {
    static make(
        type: string,
        arg1: any,
        arg2?: number | string | Item[] | null,
        arg3?: number | string | null,
        arg4?: number | string | null,
        arg5?: string | null,
        arg6?: Item[] | Item | string | null
    ): DataLayerInterface {
        switch (type.toLowerCase()) {
            case 'view_item':
                return new ViewItemDataLayer(arg1);
            case 'add_to_cart':
                return new AddToCartDataLayer(arg6 as Item[] || []);
            case 'view_cart':
                return new CartItemDataLayer(arg6 as Item[] || [], 'view_cart');
            case 'begin_checkout':
                return new CartItemDataLayer(arg6 as Item[] || [], 'begin_checkout');
            case 'checkout':
                return new CheckoutDataLayer(arg5 || '', arg6 as Item[] || []);
            case 'purchase':
                return new PurchaseDataLayer(arg3 as number || 0, arg6 as Item[] || []);
            case 'login':
                return new LoginDataLayer(arg1 as string, arg2 as string);
            case 'search':
                return new SearchDataLayer(arg1 as string);
            case 'share':
                return new ShareDataLayer(arg1 as string, arg2 as string, arg3 as string);
            case 'sign_up':
                return new SignUpDataLayer(arg1 as string, arg2 as string);
            case 'view_item_list':
                return new ViewItemListDataLayer(arg6 as Item[] || [], arg1 as string, arg2 as string);
            case 'select_item':
                return new SelectItemDataLayer(arg1 as Item, arg2 as string, arg3 as string);
            case 'view_promotion':
                return new ViewPromotionDataLayer(arg1 as Item, arg2 as string, arg3 as string, arg4 as string, arg5 as string);
            case 'select_promotion':
                return new SelectPromotionDataLayer(arg1 as Item, arg2 as string, arg3 as string, arg4 as string, arg5 as string);
            case 'add_to_wishlist':
                return new AddToWishlistDataLayer(arg6 as any);
            case 'remove_from_cart':
                return new RemoveFromCartDataLayer(arg6 as Item[] || []);
            default:
                throw new Error(`Unknown DataLayer type: ${type}`);
        }
    }
}